import React, { MutableRefObject, useEffect } from "react";
import { monaco } from "@monaco-editor/react";
import { MonacoControlledEditor } from "./MonacoControlledEditor";
import { CodeEditorProps } from "./props/codeEditorProps";
import { getLibsDefinitions } from "../services/definitions";

export const MonacoCodeEditor = ({
    mode,
    value,
    onChange,
    size,
    errorLine,
    jsSettings,
    isDarkMode,
}: Omit<CodeEditorProps, "type">) => {
    const editorRef: MutableRefObject<any> = React.useRef(null);
    useEffect(() => {
        if (mode === "js") {
            monaco
                .init()
                .then(monaco => {
                    monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
                        noSemanticValidation: true
                    });
                    monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
                        target: monaco.languages.typescript.ScriptTarget.ES2016,
                        allowNonTsExtensions: true,
                        moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
                        module: monaco.languages.typescript.ModuleKind.CommonJS,
                        noEmit: true
                    });
                });
        }
    }, []);

    useEffect(() => {
        if (mode === "js" && jsSettings) {
            monaco
                .init()
                .then(monaco => {
                    getLibsDefinitions(jsSettings)
                        .then(libs => {
                            monaco.languages.typescript.typescriptDefaults.setExtraLibs(libs);
                        });
                });
        }
    }, [JSON.stringify(jsSettings)]);
    useEffect(() => {
        if (mode === "js" && editorRef.current) {
            monaco
                .init()
                .then(monaco => {
                    const decorations = editorRef.current.getModel().getAllDecorations()
                        .filter((d: any) => d.options?.className === "monaco-error-line").
                        map((d: any) => d.id);

                    editorRef.current.deltaDecorations(decorations, []);

                    if (errorLine) {
                        editorRef.current.deltaDecorations([], [
                            {
                                range: new monaco.Range(errorLine, 1, errorLine, 1),
                                options: {
                                    isWholeLine: true,
                                    className: "monaco-error-line",
                                    glyphMarginClassName: "monaco-error-line"
                                }
                            }
                        ]);
                    }
                });
            }
    }, [errorLine]);

    const handleEditorDidMount = (_: any, editor: any) => {
        editorRef.current = editor;
    };

    useEffect(() => {
        editorRef.current?.layout();
    }, [size?.width, size?.height]);

    return <MonacoControlledEditor
        width={size?.width}
        height={size?.height}
        language={mode === "html" ? "html" : "typescript"}
        value={value}
        onChange={(_, value) => {
            onChange(value || "");
        }}
        theme={isDarkMode ? "vs-dark" : "vs"}
        editorDidMount={handleEditorDidMount}
    />;
};
